import { create } from 'zustand';
import { QuestionKey, AnswerValue, QuestionConfig } from '../constants';
import { getVisibleQuestions } from '../utils';

/**
 * Type definition for a question response that will be sent in the mutation
 */
export interface QuestionResponseInput {
  questionKey: QuestionKey;
  answerValue: AnswerValue;
}

export type QuestionResponses = Record<QuestionKey, AnswerValue | undefined>;

/**
 * State interface for the questionnaire store
 */
interface QuestionnaireState {
  // State
  currentStep: number;
  responses: QuestionResponses;

  // Actions
  setAnswer: (questionKey: QuestionKey, answerValue: AnswerValue) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  resetQuestionnaire: () => void;

  // Selectors
  getCurrentQuestion: () => QuestionConfig | undefined;
  isConditionalQuestionVisible: () => boolean;
  getVisibleQuestions: () => QuestionConfig[];
  getProgressPercentage: () => number;
  isCurrentQuestionAnswered: () => boolean;
  canGoNext: () => boolean;
  canGoBack: () => boolean;
  getAllResponses: () => QuestionResponseInput[];
}

/**
 * Convert Record responses to Map
 */
const responsesToMap = (
  responses: QuestionResponses,
): Map<QuestionKey, AnswerValue> => {
  const map = new Map<QuestionKey, AnswerValue>();

  Object.entries(responses).forEach(([key, value]) => {
    if (value !== undefined) {
      map.set(key as QuestionKey, value);
    }
  });

  return map;
};

/**
 * Store for managing questionnaire state
 * Handles question navigation, answer tracking, and progress calculation
 * Accounts for conditional question visibility based on OTC_DIAGNOSIS answer
 */
export const useQuestionnaireStore = create<QuestionnaireState>((set, get) => ({
  // Initial state
  currentStep: 0,
  responses: {} as QuestionResponses,

  setAnswer: (questionKey: QuestionKey, answerValue: AnswerValue) => {
    set((state) => {
      const newResponses = { ...state.responses };
      newResponses[questionKey] = answerValue;

      if (questionKey === QuestionKey.OtcDiagnosis) {
        if (answerValue !== AnswerValue.Yes) {
          // Clear FAMILY_SIDE response if OTC_DIAGNOSIS is NO or NOT_SURE
          delete newResponses[QuestionKey.FamilySide];
        }
      }

      return { responses: newResponses };
    });
  },

  goToNextStep: () => {
    set((state) => {
      const visibleQuestions = getVisibleQuestions(
        responsesToMap(state.responses),
      );
      const visibleCount = visibleQuestions.length;
      const nextStep = state.currentStep + 1;

      // Only increment if we haven't reached the end of visible questions
      if (nextStep < visibleCount) {
        return { currentStep: nextStep };
      }

      return state;
    });
  },

  goToPreviousStep: () => {
    set((state) => ({
      currentStep: Math.max(0, state.currentStep - 1),
    }));
  },

  resetQuestionnaire: () => {
    set({
      currentStep: 0,
      responses: {} as QuestionResponses,
    });
  },

  getCurrentQuestion: () => {
    const state = get();
    const visibleQuestions = getVisibleQuestions(
      responsesToMap(state.responses),
    );

    return visibleQuestions[state.currentStep];
  },

  isConditionalQuestionVisible: () => {
    const state = get();
    const otcDiagnosisAnswer = state.responses[QuestionKey.OtcDiagnosis];

    return otcDiagnosisAnswer === AnswerValue.Yes;
  },

  getVisibleQuestions: () => {
    const state = get();

    return getVisibleQuestions(responsesToMap(state.responses));
  },

  // Progress is based on the number of answered questions out of total visible
  // questions
  getProgressPercentage: () => {
    const state = get();
    const visibleQuestions = getVisibleQuestions(
      responsesToMap(state.responses),
    );

    if (visibleQuestions.length === 0) {
      return 0;
    }

    // Count how many visible questions have been answered
    const answeredCount = visibleQuestions.filter(
      ({ key }) => state.responses[key] !== undefined,
    ).length;

    const progress = (answeredCount / visibleQuestions.length) * 100;

    return Math.round(progress * 100) / 100; // Round to 2 decimal places
  },

  isCurrentQuestionAnswered: () => {
    const state = get();
    const currentQuestion = get().getCurrentQuestion();

    if (!currentQuestion) {
      return false;
    }

    return state.responses[currentQuestion.key] !== undefined;
  },

  canGoNext: () => {
    return (
      get().isCurrentQuestionAnswered() &&
      get().currentStep < get().getVisibleQuestions().length - 1
    );
  },

  canGoBack: () => {
    return get().currentStep > 0;
  },

  // Selector: Get all responses as ResponseInput array for mutation
  getAllResponses: () => {
    const state = get();
    const responses: QuestionResponseInput[] = [];

    Object.entries(state.responses).forEach(([questionKey, answerValue]) => {
      if (answerValue !== undefined) {
        responses.push({
          questionKey: questionKey as QuestionKey,
          answerValue,
        });
      }
    });

    return responses;
  },
}));
